// Offline playback for purchased tracks — Library only, in-app storage only
// (Cache API), never a downloadable/shareable file. Deliberately implemented
// with explicit caches.match()/put() calls from page JS rather than relying
// on the service worker intercepting <audio> network requests: WebKit/Safari
// has historically been unreliable at routing media-element fetches through
// a controlling service worker, so this approach works the same way on every
// browser, including iOS.
const AUDIO_CACHE = "metazik-audio-v1";

function cacheKeyFor(trackId) {
  // Synthetic same-origin key so we never have to reason about the real
  // audio URL's short-lived signed token (?t=...) — one stable slot per track.
  return `/__offline_audio__/${trackId}`;
}

function resolveUrl(track) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return track.audio_url.startsWith("http") ? track.audio_url : `${backendUrl}${track.audio_url}`;
}

async function hasCacheApi() {
  return typeof window !== "undefined" && "caches" in window;
}

// Fetch + store a single track's audio bytes for offline playback. Safe to
// call repeatedly (e.g. every Library load) — a fresh copy just overwrites
// the old cache entry, which also opportunistically renews it.
export async function cacheTrackForOffline(track) {
  if (!track?.id || !track?.audio_url) return;
  if (!(await hasCacheApi()) || !navigator.onLine) return;
  try {
    const resp = await fetch(resolveUrl(track), { mode: "cors" });
    if (!resp.ok) return;
    const cache = await caches.open(AUDIO_CACHE);
    await cache.put(cacheKeyFor(track.id), resp);
  } catch {
    // Best-effort — a failed prefetch just means it stays streaming-only.
  }
}

// Warm the offline cache for every track in the library. Fire-and-forget,
// small concurrency so it doesn't flood the connection on a big library.
export async function warmOfflineLibrary(tracks) {
  if (!(await hasCacheApi()) || !navigator.onLine) return;
  const CONCURRENCY = 3;
  let i = 0;
  async function worker() {
    while (i < tracks.length) {
      const track = tracks[i++];
      await cacheTrackForOffline(track);
    }
  }
  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
}

// Returns a playable URL for the given track: a local blob URL if it's
// already cached offline, otherwise the normal (network) signed URL. Also
// kicks off a background cache refresh when online so next time is covered.
export async function getPlayableUrl(track) {
  const networkUrl = resolveUrl(track);
  if (!(await hasCacheApi())) return networkUrl;

  let cached = null;
  try {
    const cache = await caches.open(AUDIO_CACHE);
    cached = await cache.match(cacheKeyFor(track.id));
  } catch {
    cached = null;
  }

  if (navigator.onLine) {
    // Online: always prefer the live stream (fresher, no local storage
    // pressure) but keep the offline copy warm in the background.
    if (track.owned || track.is_free) cacheTrackForOffline(track);
    return networkUrl;
  }

  if (cached) {
    try {
      const blob = await cached.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }

  return null; // offline and never cached — nothing we can do for this track
}

export async function isCachedOffline(trackId) {
  if (!(await hasCacheApi())) return false;
  try {
    const cache = await caches.open(AUDIO_CACHE);
    return Boolean(await cache.match(cacheKeyFor(trackId)));
  } catch {
    return false;
  }
}
